import classNames from 'classnames';
import { useMemo, useState } from 'react';
import { Upload1Icon } from 'tdesign-icons-react';
import { Button, Select, SelectValue } from 'tdesign-react';

import { Audio2, PausePurple, SoundEffect } from '@/components/icon';
import { AUDIO_ALERT } from '@/const/form';
import {
  ActionType,
  ChangeMediaOperationType,
  ChangeMediaType,
  EventType,
} from '@/const/mission';
import { IEvents } from '@/interface';
import { RichMediaValue } from '@/interface/media';
import UploadAudio from '@/material/form-upload/audio';

import styles from '../index.module.less';

interface IProps {
  contentEventIdColWidth: number | undefined;
  value: IEvents;
  arrayValue: IEvents[];
  disabled: boolean;
  onChange: (value: any) => void;
}

const BgmItem: React.FC<IProps> = (props) => {
  const { value, onChange, arrayValue, disabled, contentEventIdColWidth } = props;

  const propsValue = value?.action?.changeMedia?.operation;
  const pause = propsValue === ChangeMediaOperationType.CHANGE_MEDIA_OPERATION_TYPE_REMOVE;
  const isSoundEffect = !pause && value?.action?.changeMedia?.bgmAudio?.loopPlay === false;

  const handleChange = (data: RichMediaValue) => {
    const endType = value?.action?.changeMedia?.type;
    const isPause = data?.isPause;
    delete data?.isPause; // 暂存数据
    const bgmAudioData = (endType === ChangeMediaType.CHANGE_MEDIA_TYPE_BGM_AUDIO
      || endType === ChangeMediaType.CHANGE_MEDIA_TYPE_SOUND_EFFECT)
      ? { bgmAudio: data }
      : { bgmAudio: {} };

    const postData: IEvents = {
      type: EventType.EVENT_TYPE_ACTION,
      action: {
        type: ActionType.ACTION_TYPE_CHANGEMEDIA,
        changeMedia: {
          ...value?.action?.changeMedia,
          type: endType || ChangeMediaType.CHANGE_MEDIA_TYPE_BGM_AUDIO,
          ...bgmAudioData,
          operation: isPause
            ? ChangeMediaOperationType.CHANGE_MEDIA_OPERATION_TYPE_REMOVE
            : ChangeMediaOperationType.CHANGE_MEDIA_OPERATION_TYPE_MODIFY,
        },
      },
    };

    const ids = arrayValue
      .concat(tempVideoList)
      .map(e => e.action?.changeMedia?.bgmAudio?.url?.url);
    if (!isPause) {
      if (!ids.includes(postData.action?.changeMedia?.bgmAudio?.url?.url)) {
        setTempVideoList([...tempVideoList].concat(postData));
      }
    } else {
      setTempVideoList([...tempVideoList].concat(value));
    }

    onChange(postData);
  };

  const handleChangeSelect = (data: SelectValue) => {
    const isRemove = data === ChangeMediaOperationType.CHANGE_MEDIA_OPERATION_TYPE_REMOVE;
    const tmpData = isRemove
      ? {
        bgmAudio: {
          ...value?.action?.changeMedia?.bgmAudio,
          url: { url: '' },
        },
      }
      : { ...value?.action?.changeMedia };

    const postData: IEvents = {
      type: EventType.EVENT_TYPE_ACTION,
      action: {
        type: ActionType.ACTION_TYPE_CHANGEMEDIA,
        changeMedia: {
          ...tmpData,
          type: ChangeMediaType.CHANGE_MEDIA_TYPE_BGM_AUDIO,
          operation: data as ChangeMediaOperationType,
        },
      },
    };
    onChange(postData);
  };

  const options = useMemo(() => [
    {
      label: '背景音乐',
      value: ChangeMediaOperationType.CHANGE_MEDIA_OPERATION_TYPE_MODIFY,
    },
    {
      label: '暂停背景音乐',
      value: ChangeMediaOperationType.CHANGE_MEDIA_OPERATION_TYPE_REMOVE,
    },
  ], []);

  const bgmValue = useMemo(() => ({
    ...value?.action?.changeMedia?.bgmAudio,
    loopPlay: !isSoundEffect,
  }), [isSoundEffect, value?.action?.changeMedia?.bgmAudio]);

  const uiOptions = {
    isShowRichMedia: true,
    dialogConfig: {
      width: 547,
      height: 340,
    },
    contentStyle: { width: '100%', maxWidth: 'auto' },
    alertRender: AUDIO_ALERT,
    childRender: ({ url, name }: Record<string, string>) => {
      if (!url) {
        return (
          <Button
            disabled={disabled}
            size="small"
            theme="default"
            className={styles.imageButton}
            style={{ height: 32, flex: 1 }}
            icon={<Upload1Icon />}
          >
            点击上传BGM
          </Button>
        );
      }
      return <div className="t-input">{name}</div>;
    },
  };

  const pauseUiOptions = {
    isShowRichMedia: true,
    dialogConfig: {
      width: 547,
      height: 340,
    },
    contentStyle: { width: '100%', maxWidth: 'auto' },
    childRender: () => (
      <div className={styles.pauseAudio}>
        <PausePurple />
        <span>暂停BGM</span>
      </div>
    ),
  };

  const [tempVideoList, setTempVideoList] = useState<IEvents[]>([]);

  const getOptions = () => {
    const audio: RichMediaValue[] = [];
    let hasDefaultValue = false;
    const selectValue: RichMediaValue | undefined = value.action?.changeMedia?.bgmAudio;
    const result = tempVideoList.concat(arrayValue);
    result.forEach((item) => {
      if (item?.action?.changeMedia?.bgmAudio?.url?.url) {
        audio.push(item?.action?.changeMedia?.bgmAudio);
      }
      if (
        item?.action?.changeMedia?.bgmAudio?.url?.url
        === selectValue?.url?.url
        && item?.action?.changeMedia?.bgmAudio?.url?.name
        === selectValue?.url?.name
      ) {
        hasDefaultValue = true;
      }
    });
    if (!hasDefaultValue && selectValue) {
      audio.push(selectValue);
    }
    return {
      list: turnOption(audio) || [],
      pageInfo: {
        page: 1,
        total: 10,
        size: 10,
      },
    };
  };

  const turnOption = (audio: RichMediaValue[]) => audio?.map(e => ({
    label: e?.url?.name,
    value: e?.url?.url,
    opt: e,
  })) || [];

  const useUiOptions = pause ? pauseUiOptions : uiOptions;

  return (
    <div className={styles.itemBox}>
      {isSoundEffect
        ? <div className={classNames(styles.system, styles.leftType, 'draggable')}>
          <SoundEffect />
          <span className={styles.label}>音效</span>
        </div>
        : <div className={classNames(styles.system, styles.leftType, styles.noPadding, 'draggable')}>
          <Select
            prefixIcon={<div className={styles.selectPreIcon}>
              {pause ? <PausePurple /> : <Audio2 />}
            </div>}
            className={styles.botSelect}
            value={propsValue}
            onChange={handleChangeSelect}
            options={options}
            defaultValue={options?.[0]?.value}
            disabled={disabled}
          />
        </div>}
      <div className={styles.contentBox}>
        {pause ? (
          <div className={classNames(styles.pauseAudio, 'draggable')}>
            <span>暂停背景音乐</span>
          </div>
        ) : (
          <UploadAudio
            onChange={handleChange}
            asyncUploadOnChange={handleChange}
            value={bgmValue || {}}
            className={styles.flex1}
            {...(useUiOptions as any)}
            fetchOptions={getOptions}
            hasEditBtn={true}
            pause={pause}
            disabled={pause || disabled}
            closeCallback={() => setTempVideoList([])}
            hasEdit={false}
            hasList={true}
            hasStopAudio={true}
            contentEventIdColWidth={contentEventIdColWidth}
          />
        )}
      </div>
    </div>
  );
};

export default BgmItem;
